import {
  type Layer,
  type StandardChartLayerSpec,
  defineStandardChartLayer
} from '../../../core/layer';
import DEFINE from '../../../util/DEFINE';

const visInfo: Layer['visInfo'] = {
  id: 'positiveAndNegativeDoubleIndicatorComparisonLine',
  name_en: 'Positive and Negative Double Indicator Comparison Line',
  name_zh: '正负双指标对比折线图',
  types_en: [],
  purpose_en: [],
  purpose_zh: [],
  standers: '',
  rules: {
    data: amount => amount > 1,
    x: amount => amount > 1
  },
  /** 保证和 Spec.encoding 字段一致 */
  parameters: {
    x: {
      itemType: [DEFINE.STR, DEFINE.DATE],
      category: DEFINE.SINGLE,
      priority: DEFINE.DATE
    },
    y: {
      itemType: [DEFINE.NUMBER],
      category: DEFINE.SINGLE
    },
    y2: {
      itemType: [DEFINE.NUMBER],
      category: DEFINE.SINGLE
    }
  }
};

export interface Spec extends StandardChartLayerSpec {
  encoding: {
    x: string;
    y: string;
    y2: string;
  };
}

export const layer = defineStandardChartLayer<Spec>({
  type: visInfo.id,
  isIndependentLayer: true,
  visInfo,
  // eslint-disable-next-line max-lines-per-function
  render(api, spec, $dom) {
    const { data: dataArray, util, hook, token } = api;
    const {
      dataIndex = 0,
      encoding: { x, y, y2 }
    } = spec;
    const data = dataArray[dataIndex];
    const presetOption = hook.buildStandardChartPresetOption(api);

    // * -------------------------- data
    const dataMetaInfo = api.hook.parseDataMetaInfo(data);
    const transformData = util.dataTransform.transform(data.values.slice(), [
      { type: 'sort', field: x, order: 'ascending' },
      { type: 'deDuplication', field: x }
    ]) as (typeof data)['values'];
    const { xAxisData, yAxisData, y2AxisData } = util.dataEncoding.encoding(transformData, {
      xAxisData: { field: x },
      yAxisData: {
        field: y,
        transforms: [{ type: 'toNumeric', field: y }]
      },
      y2AxisData: {
        field: y2,
        transforms: [{ type: 'toNumeric', field: y2 }]
      }
    });

    // * -------------------------- chart
    const chartRect = $dom.getBoundingClientRect();
    const dataZoomHeight = 16;
    const gridMargin = 10 * 2;
    const heightOfSpaceOutsideGrid = dataZoomHeight + 8 + gridMargin + 14 + 8;
    const gridHeight = (chartRect.height - heightOfSpaceOutsideGrid) / 2;
    const baseGridOption = Array.isArray(presetOption.grid)
      ? presetOption.grid[0]
      : presetOption.grid!;
    const grid = [
      {
        ...baseGridOption,
        height: gridHeight
      },
      {
        ...baseGridOption,
        top: gridHeight + gridMargin,
        height: gridHeight
      }
    ];
    const [dataZoomStartIndex, dataZoomEndIndex] = [xAxisData.length - 1 - 7, xAxisData.length - 1];
    return {
      grid,
      dataZoom: [
        {
          type: 'slider',
          show: true,
          bottom: 2,
          height: dataZoomHeight,
          realtime: true,
          startValue: dataZoomStartIndex,
          endValue: dataZoomEndIndex,
          xAxisIndex: [0, 1],
          moveHandleIcon: 'path://',
          moveHandleSize: -32,
          moveHandleStyle: {
            opacity: 0
          },
          dvMoveHandleOffset: -16,
          borderRadius: 1,
          handleSize: '100%',
          textStyle: {
            color: token?.datazoomLabelColor || '#7C7D86'
          },
          dataBackground: {
            areaStyle: {
              opacity: 0
            },
            lineStyle: {
              opacity: 0
            }
          },
          selectedDataBackground: {
            areaStyle: {
              opacity: 0
            },
            lineStyle: {
              opacity: 0
            }
          }
        },
        {
          type: 'inside',
          realtime: true,
          startValue: dataZoomStartIndex,
          endValue: dataZoomEndIndex,
          xAxisIndex: [0, 1]
        }
      ],
      tooltip: {
        trigger: 'axis'
      },
      axisPointer: {
        link: [
          {
            xAxisIndex: 'all'
          }
        ]
      },
      xAxis: [
        {
          show: true,
          type: 'category',
          name: x + (dataMetaInfo[x]?.unit ? `(${dataMetaInfo[x].unit})` : ''),
          nameGap: 0,
          boundaryGap: false,
          axisLine: { onZero: true },
          data: xAxisData
        },
        {
          show: true,
          gridIndex: 1,
          type: 'category',
          boundaryGap: false,
          axisLine: { onZero: true },
          data: xAxisData,
          position: 'top',
          axisLabel: {
            show: false
          }
        }
      ],
      yAxis: [
        {
          show: true,
          dvSplitLineNumber: hook.getSplitLineNumber(gridHeight - grid[0].top - grid[0].bottom),
          name: y + (dataMetaInfo[y]?.unit ? `(${dataMetaInfo[y].unit})` : ''),
          type: 'value',
          axisLabel: {
            hideOverlap: true,
            formatter(value, index) {
              const result = hook.formatDataNumberValue(value);
              return result.value + result.unit;
            }
          }
        },
        {
          show: true,
          dvSplitLineNumber: hook.getSplitLineNumber(gridHeight - grid[0].top - grid[1].bottom),
          gridIndex: 1,
          name: y2 + (dataMetaInfo[y2]?.unit ? `(${dataMetaInfo[y2].unit})` : ''),
          nameTextStyle: {
            verticalAlign: 'bottom'
          },
          type: 'value',
          inverse: true,
          axisLabel: {
            hideOverlap: true,
            formatter(value, index) {
              const result = hook.formatDataNumberValue(value);
              return result.value + result.unit;
            }
          }
        }
      ],
      series: [
        {
          name: y,
          type: 'line',
          showSymbol: false,
          stateAnimation: {
            duration: 0
          },
          data: yAxisData
        },
        {
          name: y2,
          type: 'line',
          xAxisIndex: 1,
          yAxisIndex: 1,
          showSymbol: false,
          stateAnimation: {
            duration: 0
          },
          data: y2AxisData
        }
      ]
    };
  }
});
